import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './About.css';

gsap.registerPlugin(ScrollTrigger);

const traits = [
  { icon: '🔬', label: 'Tech Explorer' },
  { icon: '⚡', label: 'Fast Learner' },
  { icon: '🛠️', label: 'Problem Solver' },
  { icon: '🚀', label: 'Shipment First' },
];

const stats = [
  { value: '20+', label: 'Projects Completed' },
  { value: '1+', label: 'Years Experience' },
  { value: '100%', label: 'Dedication' },
  { value: '24/7', label: 'Always Available' },
];

function About() {
  const sectionRef  = useRef(null);
  const labelRef    = useRef(null);
  const titleRef    = useRef(null);
  const textRef     = useRef(null);
  const statsRef    = useRef(null);
  const traitsRef   = useRef(null);
  const imageBoxRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Set hidden state before animating in
      gsap.set([labelRef.current, titleRef.current, imageBoxRef.current], { opacity: 0 });
      gsap.set(textRef.current.querySelectorAll('p'), { opacity: 0, y: 30 });
      gsap.set(traitsRef.current.children, { opacity: 0, y: 20, scale: 0.85 });
      gsap.set(statsRef.current.children, { opacity: 0, y: 30 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });

      tl.to(labelRef.current,
        { opacity: 1, x: 0, duration: 0.6, ease: 'power3.out' }
      )
      .to(titleRef.current,
        { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }, '-=0.3'
      )
      .to(textRef.current.querySelectorAll('p'),
        { opacity: 1, y: 0, duration: 0.7, stagger: 0.15, ease: 'power3.out' }, '-=0.4'
      )
      .to(traitsRef.current.children,
        { opacity: 1, scale: 1, y: 0, duration: 0.5, stagger: 0.1, ease: 'back.out(1.5)' }, '-=0.3'
      )
      .to(imageBoxRef.current,
        { opacity: 1, x: 0, scale: 1, duration: 1, ease: 'power3.out' }, '-=0.8'
      )
      .to(statsRef.current.children,
        { opacity: 1, y: 0, duration: 0.5, stagger: 0.1, ease: 'power3.out' }, '-=0.5'
      );

      // Stat counter animation
      stats.forEach((stat, i) => {
        const el = statsRef.current?.children[i]?.querySelector('.about-stat-val');
        if (!el) return;
        const numericVal = parseFloat(stat.value);
        if (isNaN(numericVal)) return;
        const suffix = stat.value.replace(/[0-9.]/g, '');
        ScrollTrigger.create({
          trigger: statsRef.current,
          start: 'top 80%',
          once: true,
          onEnter: () => {
            const counter = { val: 0 };
            gsap.to(counter, {
              val: numericVal,
              duration: 1.5,
              ease: 'power2.out',
              onUpdate: () => {
                el.textContent = Math.round(counter.val) + suffix;
              },
            });
          },
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="about" className="about" ref={sectionRef}>
      <div className="about-bg-orb" />

      <div className="container">
        <div className="about-layout">
          {/* ─── Left: Text ─── */}
          <div className="about-left">
            <div ref={labelRef} className="section-label">Who I Am</div>

            <h2 ref={titleRef} className="section-title">
              Crafting code that<br />
              <span className="gradient-text">actually ships</span>
            </h2>

            <div ref={textRef} className="about-text">
              <p>
                I'm a hands-on full‑stack developer who builds real, working systems —
                from APIs and authentication flows to clean, responsive frontends.
                I primarily work with <strong>JavaScript, React, Next.js, Node.js,</strong> and <strong>NestJS</strong>.
              </p>
              <p>
                I spend a lot of time experimenting — converting projects to modern stacks,
                deploying on Vercel, working with JWT-based auth, integrating third-party APIs,
                and building side tools that solve real problems. I'm not afraid to break things —
                that's usually how I learn the most.
              </p>
            </div>

            {/* Trait pills */}
            <div ref={traitsRef} className="about-traits">
              {traits.map((t) => (
                <div key={t.label} className="trait-pill">
                  <span className="trait-icon">{t.icon}</span>
                  <span>{t.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ─── Right: Visual + Stats ─── */}
          <div className="about-right">
            <div ref={imageBoxRef} className="about-visual">
              <div className="about-visual-inner">
                <div className="code-block">
                  <div className="code-header">
                    <span className="dot red" /><span className="dot yellow" /><span className="dot green" />
                    <span className="code-filename">pratik.ts</span>
                  </div>
                  <pre className="code-content">{`const dev = {
  name: "Pratik Patil",
  role: "Full-Stack Developer",
  stack: [
    "React", "Next.js",
    "Node.js", "NestJS",
    "MongoDB", "PostgreSQL"
  ],
  passion: "Building things",
  status: "Open to work 🚀"
};`}</pre>
                </div>
                <div className="about-badge-floating">
                  <span>🟢</span> Currently available
                </div>
              </div>
            </div>

            {/* Stats */}
            <div ref={statsRef} className="about-stats">
              {stats.map((s) => (
                <div key={s.label} className="about-stat-card glass-card">
                  <div className="about-stat-val">{s.value}</div>
                  <div className="about-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default About;
